"use client";

import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MoveRight, Maximize2, Play, CreditCard, Users, LayoutGrid, ChevronLeft, ChevronRight, Check, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const intents = [
  {
    type: "payment",
    label: "Test a Payment",
    sub: "Would they pay for it?",
    description: "See who actually pulls out their credit card. A realistic checkout flow — no real charges.",
    bullets: ["No real payments processed", "Works on mobile", "Ready in 5 minutes"],
    icon: CreditCard,
    cta: "Create a Payment Test",
    color: "from-indigo-600 to-blue-600",
    borderAccent: "border-t-indigo-500",
    lightBg: "bg-indigo-50",
    lightBorder: "border-indigo-200",
    lightText: "text-indigo-700",
    iconColor: "text-indigo-600",
    checkColor: "text-indigo-500",
    preview: "/images/hero/hero4.png",
  },
  {
    type: "signup",
    label: "Test a Waitlist",
    sub: "Who signs up first?",
    description: "Separate real interest from empty enthusiasm. Capture emails from people who actually want in.",
    bullets: ["Collect real emails", "Embed anywhere", "Track conversion rates"],
    icon: Users,
    cta: "Create a Waitlist Test",
    color: "from-[#001a3d] to-[#002952]",
    borderAccent: "border-t-[#001a3d]",
    lightBg: "bg-[#001a3d]/5",
    lightBorder: "border-[#001a3d]/20",
    lightText: "text-[#001a3d]",
    iconColor: "text-[#001a3d]",
    checkColor: "text-[#001a3d]",
    preview: "/images/hero/hero2.png",
  },
  {
    type: "pricing_table",
    label: "Test a Price",
    sub: "What will they pay?",
    description: "Test multiple price points side by side. Find the sweet spot before you commit.",
    bullets: ["Compare up to 3 tiers", "See which tier wins", "Data-driven pricing"],
    icon: LayoutGrid,
    cta: "Create a Pricing Test",
    color: "from-violet-600 to-purple-600",
    borderAccent: "border-t-violet-500",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    lightText: "text-violet-700",
    iconColor: "text-violet-600",
    checkColor: "text-violet-500",
    preview: "/images/hero/hero3.png",
  },
];

function Hero() {
  const [titleNumber, setTitleNumber] = useState(0);
  const [activeIntent, setActiveIntent] = useState(0);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const titles = useMemo(
    () => ["Buy", "Subscribe", "Sign Up", "Convert", "Upgrade", "Join"],
    []
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setTitleNumber((prev) => (prev === titles.length - 1 ? 0 : prev + 1));
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles]);

  // Auto-cycle intent slides
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveIntent((prev) => (prev + 1) % intents.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused]);

  const goToSlide = useCallback((index: number) => {
    setActiveIntent(index);
    setIsPaused(true);
    // Resume auto-cycle after 8s of inactivity
    setTimeout(() => setIsPaused(false), 8000);
  }, []);

  const prevSlide = useCallback(() => {
    goToSlide((activeIntent - 1 + intents.length) % intents.length);
  }, [activeIntent, goToSlide]);

  const nextSlide = useCallback(() => {
    goToSlide((activeIntent + 1) % intents.length);
  }, [activeIntent, goToSlide]);

  const handleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await videoRef.current?.requestFullscreen();
      } catch (err) {
        try {
          await (videoRef.current as any)?.webkitRequestFullscreen();
        } catch (err) {
          // Fullscreen failed silently
        }
      }
    } else {
      await document.exitFullscreen();
    }
  };

  const current = intents[activeIntent];

  return (
    <div className="w-full bg-gradient-to-b from-white to-indigo-50/20 relative overflow-hidden pt-4">
      {/* Background Icons */}
      <div className="absolute inset-0 overflow-hidden opacity-[0.03] pointer-events-none">
        <div className="absolute top-[5%] left-[3%] text-4xl">{"💳"}</div>
        <div className="absolute top-[8%] right-[15%] text-5xl">{"📊"}</div>
        <div className="absolute bottom-[15%] left-[10%] text-4xl">{"📱"}</div>
        <div className="absolute top-[35%] right-[5%] text-4xl">{"📧"}</div>
        <div className="absolute bottom-[8%] right-[25%] text-5xl">{"💡"}</div>
        <div className="absolute top-[70%] left-[2%] text-4xl">{"🚀"}</div>
        <div className="absolute top-[20%] left-[30%] text-5xl">{"⚡️"}</div>
        <div className="absolute bottom-[25%] right-[10%] text-4xl">{"🔍"}</div>
        <div className="absolute top-[10%] left-[45%] text-4xl">{"💰"}</div>
        <div className="absolute bottom-[12%] left-[40%] text-5xl">{"🎯"}</div>
        <div className="absolute top-[50%] left-[65%] text-4xl">{"📈"}</div>
        <div className="absolute bottom-[35%] left-[75%] text-4xl">{"👥"}</div>
        <div className="absolute top-[65%] right-[20%] text-5xl">{"🛒"}</div>
        <div className="absolute top-[25%] right-[35%] text-4xl">{"🔒"}</div>
      </div>

      <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-6xl">
        <div className="flex flex-col items-center text-center py-12 lg:py-16 gap-10">
          {/* Header Content */}
          <div className="w-full max-w-4xl space-y-6">
            <div className="flex justify-center mb-4">
              <a href="https://fazier.com/launches/willtheyconvert-2" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=3959&badge_type=weekly&variant=2&theme=neutral"
                  width="100"
                  height="40"
                  alt="Fazier badge"
                  className="h-auto max-w-[100px]"
                />
              </a>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-gray-900">
              <span className="block">Know They'll{' '}
                <span className="inline-block relative">
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.span
                      key={titleNumber}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600"
                    >
                      {titles[titleNumber]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </span>
              <span className="block">Before You Build a Single Thing</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mt-6 min-h-[120px] md:min-h-[84px]">
              Stop guessing if your idea will sell. Create a real-looking payment page, waitlist, or pricing test in minutes. See who actually tries to buy. Build only what the market proves it wants.
            </p>

            {/* ── Intent Capture Slider (Desktop) ── */}
            <div className="hidden sm:block pt-2">
              <div className="max-w-3xl mx-auto">
                {/* Tab pills — above the card, slider-style */}
                <div className="flex justify-center gap-2 mb-4">
                  {intents.map((intent, i) => {
                    const Icon = intent.icon;
                    const isActive = i === activeIntent;
                    return (
                      <button
                        key={intent.type}
                        onClick={() => goToSlide(i)}
                        className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all duration-200 text-sm font-semibold ${
                          isActive
                            ? `${intent.lightBg} ${intent.lightBorder} border-2 shadow-sm`
                            : "border-2 border-transparent bg-gray-50 hover:bg-gray-100 text-gray-500"
                        }`}
                      >
                        <Icon className={`w-4 h-4 transition-colors ${isActive ? intent.iconColor : "text-gray-400"}`} />
                        <span className={isActive ? intent.lightText : ""}>{intent.label}</span>
                        {/* Active indicator bar */}
                        {isActive && (
                          <motion.div
                            layoutId="activeTabIndicator"
                            className={`absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-gradient-to-r ${intent.color}`}
                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Card content */}
                <div className="relative">
                  <button
                    onClick={prevSlide}
                    className="absolute -left-6 lg:-left-12 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center hover:bg-gray-50 hover:shadow-lg transition-all z-10"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current.type}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.3 }}
                      className={`rounded-2xl border border-gray-200 border-t-[3px] ${current.borderAccent} bg-white shadow-lg overflow-hidden`}
                    >
                      <div className="flex flex-row" style={{ minHeight: 300 }}>
                        {/* Content side — LEFT */}
                        <div className="w-[42%] p-6 flex flex-col justify-center">
                          <h3 className={`text-xl font-bold mb-1.5 ${current.lightText}`}>{current.sub}</h3>
                          <p className="text-sm text-gray-500 leading-relaxed mb-4">
                            {current.description}
                          </p>

                          {/* Feature bullets */}
                          <ul className="space-y-1.5 mb-5">
                            {current.bullets.map((bullet) => (
                              <li key={bullet} className="flex items-center gap-2 text-xs text-gray-600">
                                <Check className={`w-3.5 h-3.5 ${current.checkColor} flex-shrink-0`} />
                                {bullet}
                              </li>
                            ))}
                          </ul>

                          <button
                            onClick={() => navigate(`/create?type=${current.type}`)}
                            className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${current.color} text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 text-sm`}
                          >
                            {current.cta} <MoveRight className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Preview image — RIGHT */}
                        <Link
                          to={`/demos#${current.type}`}
                          className="w-[58%] relative overflow-hidden group/preview cursor-pointer block"
                        >
                          <div className={`absolute inset-0 bg-gradient-to-bl ${current.lightBg}`} />
                          <div className="relative p-5 pb-0 h-full flex items-start">
                            <motion.img
                              key={current.preview}
                              src={current.preview}
                              alt={`${current.label} preview`}
                              initial={{ opacity: 0, scale: 0.97 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.4, delay: 0.1 }}
                              className="w-full rounded-t-xl shadow-md border border-gray-100 group-hover/preview:scale-[1.02] transition-transform duration-300"
                            />
                          </div>
                          {/* Gradient fade at bottom */}
                          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover/preview:bg-black/10 transition-colors duration-200 flex items-center justify-center">
                            <div className="opacity-0 group-hover/preview:opacity-100 transition-opacity duration-200 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-md flex items-center gap-2">
                              <Eye className="w-4 h-4 text-gray-700" />
                              <span className="text-sm font-medium text-gray-700">See live demo</span>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  <button
                    onClick={nextSlide}
                    className="absolute -right-6 lg:-right-12 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center hover:bg-gray-50 hover:shadow-lg transition-all z-10"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-1.5 mt-5">
                {intents.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    className="relative h-1.5 rounded-full overflow-hidden transition-all duration-300"
                    style={{ width: i === activeIntent ? 24 : 8 }}
                  >
                    <div className="absolute inset-0 bg-gray-200 rounded-full" />
                    {i === activeIntent && (
                      <motion.div
                        className={`absolute inset-0 rounded-full bg-gradient-to-r ${current.color}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: isPaused ? 8 : 4, ease: "linear" }}
                        style={{ transformOrigin: "left" }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Secondary link: See How It Works */}
              <div className="flex pt-4 justify-center items-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-full border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all font-medium text-gray-700"
                  onClick={() => document.getElementById('how-it-works-steps')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Play className="w-3.5 h-3.5 text-indigo-600" />
                  See How It Works
                </Button>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-3">
              Start your 14-day free trial today. No payment is required now, and you can cancel at any time.
            </p>
          </div>

          {/* Video Section */}
          <div className="w-full max-w-5xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full"
            >
              <div
                ref={videoContainerRef}
                className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border border-white/10 group cursor-pointer"
                onClick={handleFullscreen}
                onMouseEnter={() => setIsHoveringVideo(true)}
                onMouseLeave={() => setIsHoveringVideo(false)}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                >
                  <source src="/videos/willtheyconvert.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>

                {/* Fullscreen overlay */}
                <AnimatePresence>
                  {isHoveringVideo && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 bg-black/20 flex items-center justify-center"
                    >
                      <motion.div
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.8 }}
                        className="bg-white/90 backdrop-blur-sm p-2 rounded-lg"
                      >
                        <Maximize2 className="w-6 h-6 text-gray-800" />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ── Intent Capture Slider (Mobile) ── */}
              <div className="mt-8 sm:hidden">
                {/* Slide content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.type}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className={`rounded-2xl border border-gray-200 border-t-[3px] ${current.borderAccent} bg-white shadow-lg overflow-hidden mb-3`}
                  >
                    {/* Preview image — fixed height with fade */}
                    <Link to={`/demos#${current.type}`} className="block relative overflow-hidden" style={{ height: 180 }}>
                      <div className={`absolute inset-0 bg-gradient-to-br ${current.lightBg}`} />
                      <div className="relative p-3 pb-0">
                        <img
                          src={current.preview}
                          alt={`${current.label} preview`}
                          className="w-full rounded-t-lg shadow-sm border border-gray-100"
                        />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    </Link>

                    {/* Content */}
                    <div className="px-4 pb-4 pt-2 text-center">
                      <p className={`text-base font-bold ${current.lightText} mb-1`}>{current.sub}</p>
                      <p className="text-gray-500 text-xs mb-3 leading-relaxed">{current.description}</p>
                      {/* Feature bullets */}
                      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mb-3">
                        {current.bullets.map((bullet) => (
                          <span key={bullet} className="flex items-center gap-1 text-[11px] text-gray-500">
                            <Check className={`w-3 h-3 ${current.checkColor}`} />
                            {bullet}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => navigate(`/create?type=${current.type}`)}
                        className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${current.color} text-white font-semibold shadow-lg text-sm`}
                      >
                        {current.cta} <MoveRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Mobile dots + nav */}
                <div className="flex justify-center items-center gap-4 mb-4">
                  <button onClick={prevSlide} className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  <div className="flex gap-1.5">
                    {intents.map((intent, i) => (
                      <button
                        key={i}
                        onClick={() => goToSlide(i)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          i === activeIntent ? `w-6 bg-gradient-to-r ${current.color}` : "w-2 bg-gray-200"
                        }`}
                      />
                    ))}
                  </div>
                  <button onClick={nextSlide} className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                {/* Mobile secondary links */}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full gap-2 rounded-full border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/50 h-12 font-medium text-base text-gray-700"
                    onClick={() => document.getElementById('how-it-works-steps')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Play className="w-4 h-4 text-indigo-600" />
                    See How It Works
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Hero };
